import React, { useState, useCallback, useEffect, useContext } from 'react';
import { Button } from 'react-bootstrap';
import { Button as MuiButton } from '@mui/material';
import { StatusIndicator } from './StatusIndicator';
import ChartContainer from './ChartContainer';
import BinaryIndicatorBlock from './BinaryIndicatorBlock';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import { ArrowBack, ChevronRight } from '@mui/icons-material';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import { IndicatorsContext } from '../State/IndicatorsContext';
import { GoalsContext } from '../State/GoalsContext';
import { formatTrendScore, formatStatusScore } from '../utils/scoreFormatUtils';
import { hasKeyFigure, formatKeyFigureDisplay, shouldShowKeyValuesSection } from '../utils/keyFigures';
import { getStatusColor } from '../utils/euStatusUtils';
import '../utils/sharedStyles.css';

const IndicatorsListForTargetMobile = ({ indicators }) => {
  const [selectedIndicator, setSelectedIndicator] = useState(null);
  const [fullIndicatorData, setFullIndicatorData] = useState(null);
  const [chartRefs, setChartRefs] = useState({});
  const { fetchSingleIndicator } = useContext(IndicatorsContext);
  const { goalType } = useContext(GoalsContext);

  // Get trend arrow and color
  const getTrendArrow = (trend) => {
    if (!trend) return { arrow: '—', color: '#bdbdbd' };
    const cat = String(trend).toLowerCase();
    if (cat.includes('on track') || cat.includes('target met')) {
      return { arrow: '↑', color: '#16a34a' };
    } else if (cat.includes('moderate')) {
      return { arrow: '↗', color: '#fcc30c' };
    } else if (cat.includes('marginal') || cat.includes('stagnation') || cat.includes('stagnating')) {
      return { arrow: '→', color: '#f67c01' };
    } else if (cat.includes('regression') || cat.includes('decreasing')) {
      return { arrow: '↓', color: '#d3302f' };
    }
    return { arrow: '—', color: '#bdbdbd' };
  };

  const handleIndicatorClick = (indicator) => {
    setSelectedIndicator(indicator);
    setFullIndicatorData(null); // Clear previous full data
    window.scrollTo(0, 0);
  };

  const handleBackToList = () => {
    setSelectedIndicator(null);
    setFullIndicatorData(null);
  };

  // Always fetch full indicator preview when selected to ensure we have latest assessment data
  useEffect(() => {
    if (selectedIndicator?.id) {
      // Always fetch fresh preview data to ensure we have the latest assessment
      fetchSingleIndicator(selectedIndicator.id, goalType).then(fullData => {
        if (fullData) {
          setFullIndicatorData(fullData);
        } else {
          // Fallback to indicator from list if preview fetch fails
          setFullIndicatorData(selectedIndicator);
        }
      });
    } else {
      setFullIndicatorData(null);
    }
  }, [selectedIndicator?.id, goalType, fetchSingleIndicator]);

  // Download functions
  const handleDownload = (indicatorId, type) => {
    const chartElement = chartRefs[indicatorId];
    if (!chartElement) return;

    html2canvas(chartElement, { useCORS: true, backgroundColor: "#fff" }).then(canvas => {
      canvas.toBlob(blob => {
        saveAs(blob, `indicator-${indicatorId}.png`);
      });
    });    
  };

  const handleDownloadData = (indicator) => {
    console.log('🔄 Mobile CSV Download clicked for indicator:', indicator?.id);
    console.log('📊 Mobile Indicator data:', indicator?.acf);
    
    const csvContent = generateCSV(indicator);
    console.log('📝 Mobile Generated CSV content:', csvContent);
    console.log('📏 Mobile CSV length:', csvContent.length);
    
    if (csvContent.length === 0) {
      console.error('❌ Mobile Empty CSV content generated');
      return;
    }
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    console.log('💾 Mobile Blob created:', blob);
    saveAs(blob, `indicator-${indicator.id}-data.csv`);
    console.log('✅ Mobile CSV download initiated');
  };

  const generateCSV = (indicator) => {
    let csvContent = '';
    const isBinaryIndicator = indicator?.indicatorType === 'binary';

    if (isBinaryIndicator && indicator?.chartData?.labels?.length && indicator?.chartData?.datasets?.[0]?.data) {
      const labels = indicator.chartData.labels;
      const values = indicator.chartData.datasets[0].data;
      csvContent += 'Year,Status\n';
      labels.forEach((year, index) => {
        const v = values[index];
        const status = v != null && Number(v) >= 0.5 ? 'Yes' : 'No';
        csvContent += `${year},${status}\n`;
      });
      return csvContent;
    }

    const dataType = indicator?.acf?.data_display_type || indicator?.acf?.graph_data_indicators?.data_display_type;

    if (dataType === 'line-graph-bar-chart' || indicator?.chartData) {
      try {
        if (indicator?.chartData) {
          // Use chartData from preview endpoint
          const labels = indicator.chartData.labels || [];
          const datasets = indicator.chartData.datasets || [];
          
          // Header
          const header = ['Year', ...datasets.map(d => d.label || 'Series')];
          csvContent += header.join(',') + '\n';
          
          // Rows
          labels.forEach((year, index) => {
            const row = [year];
            datasets.forEach(dataset => {
              row.push(dataset.data?.[index] || '');
            });
            csvContent += row.join(',') + '\n';
          });
        } else {
          // Fallback to whole_data parsing
          const rawWhole = indicator?.acf?.graph_data_indicators?.whole_data || indicator?.acf?.whole_data || '';
          const mappedObject = JSON.parse(rawWhole);

          const labelsObject = mappedObject.columns?.find(column => column.label === "Year");
          const labelsArray = labelsObject ? labelsObject.data : (mappedObject.columns?.[0]?.data || []);
          const otherLabels = mappedObject.columns?.filter(column => column.label !== "Year") || [];

          // Header
          const header = ['Year', ...otherLabels.map(col => col.label)];
          csvContent += header.join(',') + '\n';

          // Rows
          labelsArray.forEach((year, index) => {
            const row = [year];
            otherLabels.forEach(col => {
              row.push(col.data?.[index] || '');
            });
            csvContent += row.join(',') + '\n';
          });
        }
      } catch (error) {
        console.error('Error generating CSV:', error);
        csvContent = 'Indicator,Value,Description\n';
        csvContent += `"${indicator.acf.index}","${indicator.title_render}","${indicator.acf.description}"\n`;
      }
    } else {
      // Fallback for other data types
      csvContent = 'Indicator,Value,Description\n';
      csvContent += `"${indicator.acf.index}","${indicator.title_render}","${indicator.acf.description}"\n`;
    }
    
    return csvContent;
  };

  // Memoize the setChartRef function to prevent infinite re-renders
  const handleSetChartRef = useCallback((ref) => {
    if (selectedIndicator) {
      setChartRefs(prev => ({ ...prev, [selectedIndicator.id]: ref }));
    }
  }, [selectedIndicator?.id]);

  // If an indicator is selected, show detailed view
  // Use fullIndicatorData if available, otherwise fall back to selectedIndicator
  const displayIndicator = fullIndicatorData || selectedIndicator;
  
  if (selectedIndicator) {
    return (
      <div className="mobile-indicator-detail">
        <div className="mobile-header mobile-header--nested">
          <Button
            variant="link"
            onClick={handleBackToList}
            className="mobile-back-btn"
          >
            <ArrowBack fontSize="small" /> Back to Indicators
          </Button>
        </div>

        {/* Same section structure as SingleIndicatorDetails — padding from parent shell */}
        <div className="mobile-detail-content mobile-detail-content--nested">
          <div className="indicator-details-container">
            <div className="card-container">
              {(() => {
                const isUN = goalType === 'un';
                const hasNumericTarget = displayIndicator?.acf?.has_numeric_target || false;
                const statusIsNotApplicable = !displayIndicator?.computed?.semaphore_status ||
                  String(displayIndicator?.computed?.semaphore_status || '').toLowerCase().includes('not applicable');
                const showNoTargetMessage = isUN ? false : (!hasNumericTarget || statusIsNotApplicable);
                const showStatusSection = !isUN && displayIndicator?.computed?.semaphore_status && hasNumericTarget && !statusIsNotApplicable;
                const statusColor = getStatusColor(displayIndicator?.computed?.semaphore_status);
                const statusScore = displayIndicator?.computed?.status_score || 0;
                const trendInfo = displayIndicator?.computed?.trend ? getTrendArrow(displayIndicator.computed.trend) : null;
                const showKeyValues = shouldShowKeyValuesSection(displayIndicator?.computed);
                const hasProgressContent = displayIndicator?.computed?.trend || showStatusSection || showNoTargetMessage || showKeyValues;
                const hasChartData = displayIndicator?.chartData?.labels?.length > 0 && displayIndicator?.chartData?.datasets?.length > 0;
                const isBinary = displayIndicator?.indicatorType === 'binary';
                const metadataList = displayIndicator?.acf?.metadata || displayIndicator?.acf?.indicator_metadata || [];
                const metadataHasSourceLink = Array.isArray(metadataList) && metadataList.some(
                  (item) => item?.key === 'Source' && item?.is_link
                );
                const hasReferencesContent = metadataList.length > 0 ||
                  displayIndicator?.acf?.source || displayIndicator?.acf?.reference_field;

                return (
                  <>
                    <section className="indicator-detail-section indicator-detail-section--about">
                      <h2 className="title-h2 indicator-detail-title">
                        {displayIndicator.title_render || displayIndicator.title || 'Indicator'}
                      </h2>
                      {displayIndicator?.acf?.description && (
                        <div className="indicator-detail-description">
                          <h6 className="sdg-section-title">Description</h6>
                          <p className="text-body indicator-detail-description-text">{displayIndicator.acf.description}</p>
                        </div>
                      )}
                    </section>

                    {hasProgressContent && (
                      <section className="indicator-detail-section indicator-detail-section--progress" aria-label="Progress and assessment">
                        <h6 className="sdg-section-title indicator-detail-section-heading">Progress & assessment</h6>
                        <div className="target-assessment-container">
                        <div className="assessment-box indicator-assessment-block target-assessment-col">
                          <h6 className="assessment-box-title">Progress</h6>
                          {showStatusSection && (
                            <div className="indicator-progress-at-a-glance">
                              <div className="indicator-progress-item">
                                <span className="indicator-progress-label">Status</span>
                                <span className="badge-pill">{displayIndicator.computed.semaphore_status}</span>
                                {displayIndicator.computed.status_score != null && (
                                  <span className="text-muted">({formatStatusScore(displayIndicator.computed.status_score)} / 100)</span>
                                )}
                              </div>
                            </div>
                          )}
                          {displayIndicator?.computed?.trend && trendInfo && (
                            <div className="indicator-progress-detail">
                              <span style={{ color: trendInfo.color, fontSize: '1.25rem', fontWeight: 900, marginRight: '6px' }}>{trendInfo.arrow}</span>
                              <span className="badge-pill-trend">{displayIndicator.computed.trend}</span>
                              {displayIndicator.computed.trend_score != null && (
                                <span className="text-muted"> (score {formatTrendScore(displayIndicator.computed.trend_score)} / 5)</span>
                              )}
                            </div>
                          )}
                          {showStatusSection && (
                            <div className="indicator-progress-detail indicator-progress-detail--status">
                              <span className="indicator-progress-detail-label">Distance to target</span>
                              <div className="status-bar-container">
                                <span className="status-bar-fill" style={{ background: statusColor, width: `${Math.min(100, Math.max(0, statusScore))}%` }} />
                              </div>
                            </div>
                          )}
                          {showNoTargetMessage && (
                            <div className="indicator-status-not-available">
                              <div className="indicator-status-not-available-inner">
                                <p className="indicator-status-not-available-title">Status not shown</p>
                                <p className="indicator-status-not-available-text">
                                  Distance-to-target status needs a numeric target. This indicator does not have one configured.
                                  {isUN ? ' UN indicators show progress only.' : ''}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                        {showKeyValues && (
                          <div className="assessment-box indicator-assessment-block target-assessment-col">
                            <h6 className="assessment-box-title">Key values</h6>
                            <div className="key-values-grid">
                              {hasKeyFigure(displayIndicator?.computed?.baseline) && (
                                <div className="key-value-item">
                                  <div className="key-value-item-label">Baseline</div>
                                  <div className="key-value-item-value key-value-item-value--baseline">
                                    {formatKeyFigureDisplay(displayIndicator.computed.baseline, isBinary)}
                                  </div>
                                </div>
                              )}
                              {hasKeyFigure(displayIndicator?.computed?.latest) && (
                                <div className="key-value-item">
                                  <div className="key-value-item-label">Latest</div>
                                  <div className="key-value-item-value key-value-item-value--latest">
                                    {formatKeyFigureDisplay(displayIndicator.computed.latest, isBinary)}
                                  </div>
                                </div>
                              )}
                              {displayIndicator?.computed?.target && displayIndicator.computed.target.value != null && (
                                <div className="key-value-item key-value-item--target">
                                  <div className="key-value-item-label">Target</div>
                                  <div className="key-value-item-value key-value-item-value--target key-value-item-value--target-with-legend">
                                    <span className="chart-target-legend-line chart-target-legend-line--inline" aria-hidden="true" />
                                    <span className="key-value-item-value--target-number">{displayIndicator.computed.target.value}</span>
                                    {displayIndicator.computed.target.year != null && <span className="key-value-item-value--target-year"> by {displayIndicator.computed.target.year}</span>}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        </div>
                      </section>
                    )}

                    {(hasChartData || isBinary) && (
                      <section className="indicator-detail-section indicator-detail-section--data" aria-label="Data and chart">
                        <h6 className="sdg-section-title indicator-detail-section-heading">Data</h6>
                        {displayIndicator?.chartSeriesDescription && String(displayIndicator.chartSeriesDescription).trim() !== '' && (
                          <aside className="indicator-series-description-panel" style={{ marginBottom: '12px' }} aria-label="Series description">
                            <div className="indicator-series-description-panel__title">Series description</div>
                            <p className="indicator-series-description-panel__body">{String(displayIndicator.chartSeriesDescription).trim()}</p>
                          </aside>
                        )}
                        <div className="indicator-chart-wrapper">
                          {isBinary ? (
                            <BinaryIndicatorBlock indicator={displayIndicator} />
                          ) : (
                            <ChartContainer indicator={displayIndicator} chartType={displayIndicator.chartType} chartData={displayIndicator.chartData} setChartRef={handleSetChartRef} />
                          )}
                        </div>
                        {!isBinary && displayIndicator?.moeEnabled && typeof displayIndicator?.moePercent === 'number' && (
                          <div className="indicator-moe-container">
                            <span className="indicator-moe-tag">Margin of error: {displayIndicator.moeMode === 'up' ? '+' : displayIndicator.moeMode === 'down' ? '−' : '±'}{displayIndicator.moePercent}%</span>
                          </div>
                        )}
                        <div className="indicator-detail-downloads indicator-detail-downloads--mobile">
                          <MuiButton startIcon={<FileDownloadOutlined />} variant="outlined" size="medium" onClick={() => handleDownloadData(displayIndicator)} fullWidth className="indicator-download-btn">Download CSV</MuiButton>
                          {!isBinary && (
                            <MuiButton startIcon={<FileDownloadOutlined />} variant="outlined" size="medium" onClick={() => handleDownload(displayIndicator.id, 'image')} fullWidth className="indicator-download-btn">Download PNG</MuiButton>
                          )}
                        </div>
                      </section>
                    )}

                    {hasReferencesContent && (
                      <section className="indicator-detail-section indicator-detail-section--references" aria-label="Source and metadata">
                        <h6 className="sdg-section-title indicator-detail-section-heading">Source & metadata</h6>
                        <div className="indicator-references-content">
                          {displayIndicator.acf.reference_field && (
                            <div className="indicator-reference-block">
                              <span className="indicator-reference-label">Reference</span>
                              <p className="indicator-reference-text">{displayIndicator.acf.reference_field}</p>
                            </div>
                          )}
                          {displayIndicator.acf.source && !metadataHasSourceLink && (
                            <div className="indicator-reference-block">
                              <span className="indicator-reference-label">Source</span>
                              <a href={displayIndicator.acf.source} target="_blank" rel="noopener noreferrer" className="metadata-link">{displayIndicator.acf.source}</a>
                            </div>
                          )}
                          {metadataList.length > 0 && (
                            <div className="metadata-container indicator-metadata">
                              <div className="metadata-list">
                                {metadataList.map((item, i) => (
                                  <div key={i} className="metadata-item">
                                    <div className="metadata-key">{item.key}</div>
                                    <div className="metadata-value">{item.is_link ? <a href={item.value} target="_blank" rel="noopener noreferrer" className="metadata-link">{item.link_label || item.value}</a> : item.is_html ? <div dangerouslySetInnerHTML={{ __html: item.value }} /> : <span>{item.value}</span>}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </section>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show indicators list (mobile version)
  return (
    <div className="mobile-indicators-list">
      {indicators.length > 0 && (
        <div className="mobile-section-header">
          <h5>Indicators ({indicators.length})</h5>
        </div>
      )}
      
      <div className="mobile-list">
        {indicators.map((indicator) => (
          <div 
            key={indicator.id}
            className="mobile-list-item"
            onClick={() => handleIndicatorClick(indicator)}
          >
            <div className="mobile-list-content">
              <div className="mobile-list-main">
                <div className="mobile-list-title">
                  <strong>{indicator.acf.index}</strong>
                </div>
                <div className="mobile-list-subtitle">
                  {indicator.title_render}
                </div>
              </div>
              <div className="mobile-list-status">
                <StatusIndicator status={indicator.acf.status} />
              </div>
              <div className="mobile-list-arrow">
                <ChevronRight />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default IndicatorsListForTargetMobile;
